/**
 * De schermen buiten de app (inloggen, aanmelden, de code, een nieuw
 * wachtwoord, de korte pagina's over heute) in ontwerp v4: het zand, het
 * merk bovenaan als de weg terug, de titel als zin, velden als
 * doorschijnend vlak met hun naam erboven, één volle knop en de rest
 * zacht (--fill). De vorm staat in heute.css (.auth-*, .veld-*, .zin-*).
 *
 * Alleen de kleren: conform, de honeypots en elke catalogussleutel blijven
 * zoals ze waren, dus de label- en rolselectors van de e2e-toetsen blijven
 * passen. De velden en knoppen staan ook in Instellingen (op papier).
 */
import { HeuteMerk } from './merk.tsx'

export function AuthShell({
	title,
	sub,
	rechts,
	children,
}: {
	title: React.ReactNode
	sub?: React.ReactNode
	/** Rechts in de kop, naast het merk (de voorpagina: Log in). */
	rechts?: React.ReactNode
	children?: React.ReactNode
}) {
	return (
		<main className="auth-scherm">
			<div className="auth-kolom pad">
				<header className="auth-kop">
					<HeuteMerk naar="/" />
					{rechts}
				</header>
				<div className="auth-inhoud fade-up">
					<h1 className="zin-kop">{title}</h1>
					{sub ? <p className="zin-sub">{sub}</p> : null}
					{children ? <div style={{ marginTop: 28 }}>{children}</div> : null}
				</div>
			</div>
		</main>
	)
}

type FieldProps = {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: Array<string | null | undefined> | null
}

export function AuthField({ labelProps, inputProps, errors }: FieldProps) {
	const errorId = errors?.length ? `${inputProps.id}-errors` : undefined
	return (
		<div className="veld">
			<label
				{...labelProps}
				htmlFor={labelProps.htmlFor ?? inputProps.id}
				className="veld-naam"
			/>
			<input
				{...inputProps}
				aria-describedby={errorId}
				aria-invalid={errorId ? true : inputProps['aria-invalid']}
				className="veld-invoer"
			/>
			<AuthErrors errors={errors} id={errorId} />
		</div>
	)
}

/** Een gewoon vinkje: rol- en labelselectors blijven werken. De hele regel
 *  is het tikdoel (48 hoog). */
export function AuthCheckbox({ labelProps, inputProps, errors }: FieldProps) {
	const errorId = errors?.length ? `${inputProps.id}-errors` : undefined
	return (
		<div>
			<label
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: 10,
					minHeight: 'var(--target)',
					fontFamily: 'var(--font-sans)',
					fontWeight: 'var(--fw-label)',
					fontSize: 15,
					color: 'var(--ink)',
					cursor: 'pointer',
				}}
			>
				<input
					type="checkbox"
					{...inputProps}
					aria-describedby={errorId}
					style={{
						width: 20,
						height: 20,
						margin: 0,
						flexShrink: 0,
						accentColor: 'var(--ink)',
					}}
				/>
				{labelProps.children}
			</label>
			<AuthErrors errors={errors} id={errorId} />
		</div>
	)
}

/** Een fout in woorden, met een × ervoor: nooit alleen de rode rand. */
export function AuthErrors({
	errors,
	id,
}: {
	errors?: Array<string | null | undefined> | null
	id?: string
}) {
	const list = errors?.filter(Boolean) ?? []
	if (list.length === 0) return null
	return (
		<ul id={id} className="veld-fout">
			{list.map((error) => (
				<li key={error}>{error}</li>
			))}
		</ul>
	)
}

/** De knop: primary is de ene volle knop (inkt), ghost is zacht (--fill),
 *  danger is het gevaar-vlak (--danger op --danger-bg). Bezig maakt hem
 *  onbruikbaar, zonder draaiend wieltje. */
export function AuthButton({
	tone = 'primary',
	pending = false,
	style,
	className,
	children,
	...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
	tone?: 'primary' | 'ghost' | 'danger'
	pending?: boolean
}) {
	const toon =
		tone === 'primary'
			? 'btn-primary'
			: tone === 'danger'
				? 'btn-gevaar'
				: 'btn-zacht'
	return (
		<button
			{...props}
			disabled={props.disabled || pending}
			className={`btn ${toon}${className ? ` ${className}` : ''}`}
			style={{
				width: '100%',
				minHeight: 'var(--size-knop)',
				fontSize: 16,
				...style,
			}}
		>
			{children}
		</button>
	)
}

/** Ruimte tussen het formulier en de andere manieren om binnen te komen. */
export function AuthDivider() {
	return <hr style={{ height: 28, margin: 0, border: 0 }} />
}

/** De regel onder het formulier, bijvoorbeeld de weg naar aanmelden. */
export function AuthFootnote({ children }: { children: React.ReactNode }) {
	return <p className="auth-voet">{children}</p>
}
